import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Upload, FileText, AlertCircle, CheckCircle, Clock, X, User, LogOut } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_videogist-3/artifacts/809w15is_summbee%20logo_just%20bee.png";

function BatchProcessingPage() {
  const { user, subscription, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [videoUrls, setVideoUrls] = useState("");
  const [uploading, setUploading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const isEnterprise = subscription?.effective_plan === "creator";

  useEffect(() => {
    if (!isEnterprise) {
      navigate("/pricing");
      return;
    }
    fetchJobs();
  }, [isEnterprise]);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/batch`, { withCredentials: true });
      setJobs(response.data.jobs || []);
    } catch (error) {
      console.error("Error fetching batch jobs:", error);
      if (error.response?.status === 403) {
        navigate("/pricing");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const urls = videoUrls
      .split("\n")
      .map(url => url.trim())
      .filter(url => url.length > 0);

    if (urls.length === 0) {
      toast.error("Please enter at least one video URL");
      return;
    }

    if (urls.length > 100) {
      toast.error("Maximum 100 videos per batch");
      return;
    }

    setUploading(true);
    try {
      const response = await axios.post(
        `${API}/batch`,
        { video_urls: urls },
        { withCredentials: true }
      );
      
      toast.success(response.data.message);
      setVideoUrls("");
      fetchJobs();
      
      // Navigate to job detail
      navigate(`/batch/${response.data.job_id}`);
    } catch (error) {
      console.error("Error creating batch job:", error);
      toast.error(error.response?.data?.detail || "Failed to create batch job");
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      // Parse CSV or text file
      const urls = text.split(/[\n,]/).map(url => url.trim()).filter(url => url.length > 0);
      setVideoUrls(urls.join("\n"));
    };
    reader.readAsText(file);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="status-icon success" size={20} />;
      case "processing":
        return <Clock className="status-icon processing" size={20} />;
      case "failed":
        return <AlertCircle className="status-icon error" size={20} />;
      default:
        return <Clock className="status-icon" size={20} />;
    }
  };

  const getStatusText = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="loading-state">Loading...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <nav className="navbar">
        <div className="nav-content">
          <div className="nav-logo" onClick={() => navigate("/")} style={{cursor: "pointer"}}>
            <img src={LOGO_URL} alt="SummBee" className="logo-image" />
            <span>SummBee</span>
          </div>
          <div className="nav-links">
            <Button onClick={() => navigate("/dashboard")} variant="ghost">
              Dashboard
            </Button>
            <Button onClick={() => navigate("/library")} variant="ghost">
              My Videos
            </Button>
            <Button onClick={() => navigate("/batch")} variant="ghost" className="active">
              Batch Processing
            </Button>
            <div className="user-info">
              <User size={16} />
              <span>{user?.name}</span>
            </div>
            <Button onClick={logout} variant="ghost" className="logout-button">
              <LogOut size={16} /> Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="dashboard-container">
        <div className="page-header">
          <div>
            <h1 className="page-title">Batch Processing</h1>
            <p className="page-subtitle">Process up to 100 videos at once</p>
          </div>
        </div>

        {/* Upload Section */}
        <Card className="batch-upload-card">
          <CardHeader>
            <CardTitle>Submit Batch Job</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="batch-upload-content">
              <div className="upload-methods">
                <div className="method-option">
                  <FileText size={24} />
                  <p>Paste URLs (one per line)</p>
                </div>
                <div className="method-divider">OR</div>
                <div className="method-option">
                  <Upload size={24} />
                  <label htmlFor="csv-upload" className="upload-label">
                    Upload CSV/TXT
                    <input
                      id="csv-upload"
                      type="file"
                      accept=".csv,.txt"
                      onChange={handleFileUpload}
                      style={{ display: "none" }}
                    />
                  </label>
                </div>
              </div>

              <Textarea
                value={videoUrls}
                onChange={(e) => setVideoUrls(e.target.value)}
                placeholder="https://youtube.com/watch?v=...&#10;https://vimeo.com/...&#10;https://youtube.com/watch?v=..."
                rows={10}
                className="url-textarea"
              />

              <div className="url-count">
                {videoUrls.split("\n").filter(url => url.trim().length > 0).length} / 100 videos
              </div>

              <Button
                onClick={handleSubmit}
                disabled={uploading || !videoUrls.trim()}
                className="submit-batch-button"
              >
                {uploading ? "Creating Job..." : "Start Batch Processing"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Previous Jobs */}
        {jobs.length > 0 && (
          <Card className="batch-jobs-card">
            <CardHeader>
              <CardTitle>Batch Jobs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="jobs-list">
                {jobs.map((job) => (
                  <div
                    key={job.id}
                    className="job-item"
                    onClick={() => navigate(`/batch/${job.id}`)}
                  >
                    <div className="job-info">
                      {getStatusIcon(job.status)}
                      <div className="job-details">
                        <div className="job-status">{getStatusText(job.status)}</div>
                        <div className="job-meta">
                          {job.processed_videos} / {job.total_videos} videos processed
                          {job.status === "completed" && (
                            <span className="success-count">
                              • {job.successful_videos} successful
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="job-date">
                      {new Date(job.created_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default BatchProcessingPage;
